// src/components/lessons/renderers/VideoRenderer.jsx
import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { trackEvent } from '../../../firebase';

const VideoRenderer = ({ block, isMobile }) => {
  const [loaded, setLoaded] = useState(false);
  const { videoId, title } = block;

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1`;

  return (
    <div style={{
      marginBottom: isMobile ? '1.5rem' : '2rem',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      border: '2px solid #000000',
      boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
      background: '#000',
    }}>
      {/* Label */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.25rem',
        background: '#000000',
        borderBottom: '2px solid #000000',
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: '#FF0000',
          flexShrink: 0,
        }} />
        <span style={{
          fontSize: isMobile ? '0.75rem' : '0.8rem',
          fontWeight: '700',
          color: '#FFFFFF',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}>
          Video Lesson
        </span>
        {title && (
          <>
            <span style={{ color: '#666', fontSize: '0.75rem' }}>·</span>
            <span style={{
              fontSize: isMobile ? '0.78rem' : '0.82rem',
              color: '#AAAAAA',
              fontWeight: '500',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {title}
            </span>
          </>
        )}
      </div>

      {/* Video area */}
      <div style={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%', // 16:9
        background: '#000',
      }}>
        {!loaded ? (
          /* Thumbnail with play button */
          <button
            onClick={() => {
              setLoaded(true);
              trackEvent('video_play', { video_id: videoId, video_title: title || '' });
            }}
            aria-label={`Play video: ${title || 'Video Lesson'}`}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              background: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={thumbnailUrl}
              alt={title || 'Video thumbnail'}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.85,
              }}
              onError={(e) => {
                // Fallback to hqdefault if maxresdefault not available
                e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
              }}
            />
            {/* Play button overlay */}
            <div style={{
              position: 'relative',
              zIndex: 1,
              width: isMobile ? '56px' : '68px',
              height: isMobile ? '56px' : '68px',
              borderRadius: '50%',
              background: '#3A86FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 24px rgba(58,134,255,0.5)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'scale(1.08)';
                e.currentTarget.style.boxShadow = '0 6px 32px rgba(58,134,255,0.7)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(58,134,255,0.5)';
              }}
            >
              <Play style={{
                width: isMobile ? '22px' : '28px',
                height: isMobile ? '22px' : '28px',
                color: '#FFFFFF',
                marginLeft: '3px', // optical centering
                fill: '#FFFFFF',
              }} />
            </div>
          </button>
        ) : (
          <iframe
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            src={embedUrl}
            title={title || 'Video Lesson'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
    </div>
  );
};

export default VideoRenderer;
